<template>
  <div class="product-varieties-area">
    <div class="product-varieties-head">
      <img :src="title.pc" class="product-varieties-head_title pc-only" :alt="title.alt" />
      <img :src="title.sp" class="product-varieties-head_title sp-only" :alt="title.alt" />
      <img v-if="headImage" :src="headImage" class="product-varieties-head_products pc-only" alt="" />
    </div>

    <!-- PC -->
    <div class="product-varieties-body">
      <div class="product-varieties-body_title-box">
        <p v-for="label in labels" :key="label.text" class="product-varieties-label" :class="`product-varieties-label_${label.color}`">
          {{ label.text }}
        </p>
      </div>
      <div class="product-varieties-body_content-box">
        <NuxtLink v-for="item in items" :key="item.to" :to="item.to" class="product-varieties-body_content-item">
          <img :src="item.image" :alt="item.name" />
          <img v-if="item.badge" :src="item.badge" class="product-varieties-badge" alt="人気 No.1" />
        </NuxtLink>
      </div>
    </div>

    <!-- SP。PC と同じ内容を縦積みにした別マークアップ -->
    <div class="product-varieties-sp_body">
      <div v-for="item in items" :key="item.to" class="product-varieties-sp_body-item">
        <p>
          <template v-for="(line, index) in item.note" :key="index">
            {{ line }}<br v-if="index < item.note.length - 1" />
          </template>
        </p>
        <NuxtLink :to="item.to" class="product-varieties-sp_body-item_card">
          <img :src="item.spImage" :alt="item.name" />
          <img v-if="item.badge" :src="item.badge" class="product-varieties-sp_badge" alt="人気 No.1" />
        </NuxtLink>
      </div>
    </div>

    <div v-if="note.length" class="product-varieties-body_note-box">
      <img :src="noteIcon" alt="" />
      <p>
        <template v-for="(line, index) in note" :key="index">
          {{ line }}<br v-if="index < note.length - 1" />
        </template>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-varieties-area`（エパゴールド3種の比較・相互送客）。
 * **`549`（小粒タイプ）／`558`（300）／`559`（600）の3商品にのみ出る**。
 * §3.3.1 の13点に入っていなかった**14番目の部品**（決定#62）。
 *
 * **PC と SP で別マークアップを持つ。** PC は `product-varieties-body`（ラベル2枚＋横並びの
 * 商品画像3点）、SP は `product-varieties-sp_body`（説明文＋縦積みのカード）。同じ内容を2組の
 * DOM で出す作りで、支給CSS が幅で出し分ける。
 *
 * **既存サイトにも同じ比較はあるが画像1枚**（`productContents/{549,558,559}/commitmentMore.vue`
 * が `/image/product/559/detail_epagold_03.jpg` を貼るだけでリンク無し）。
 * **新デザインはこれを HTML に分解して3商品を相互リンクさせる＝送客導線の新設。**
 *
 * **飛び先は支給が全数 `href="#"`** だが、`alt` が `ラフィーネエパゴールド 600` などと商品名
 * そのままなので `/products/559` `/products/558` `/products/549` の相互リンクで確定させる（決定#33）。
 *
 * 画像は3商品で共有するため `/image/product/common/` に置き、**フルパスで渡す**（決定#64）。
 * 「人気 No.1」バッジは支給では3本すべてに入る。
 * → CONVERSION-GUIDE.md 決定#62
 */
type VarietyItem = {
  /** 商品名（`alt` に使う） */
  name: string;
  /** 遷移先。`/products/559` など */
  to: string;
  /** PC 用の商品画像 */
  image: string;
  /** SP 用の商品画像 */
  spImage: string;
  /** SP 側に出る説明。1要素＝1行 */
  note: string[];
  /** 「人気 No.1」バッジの画像。無ければ省略 */
  badge?: string;
};

type Props = {
  /** 見出し画像（PC / SP） */
  title: { pc: string; sp: string; alt: string };
  /** 見出し横の商品集合画像（PC のみ） */
  headImage?: string;
  /** PC 側の吹き出しラベル */
  labels?: { text: string; color: 'pink' | 'green' }[];
  items: VarietyItem[];
  /** 下部の注記。1要素＝1行 */
  note?: string[];
  noteIcon?: string;
};

withDefaults(defineProps<Props>(), {
  headImage: '',
  labels: () => [],
  note: () => [],
  noteIcon: '/image/product/common/lump.png',
});
</script>
